/**
 * SessionKeybookPopover — shows active and pending credentials for the current agent session.
 *
 * Appears when clicking the BookKey icon next to the attachment button.
 * - Pending: credential forms that haven't been filled yet
 * - Active: credentials loaded in this session (env var names + labels)
 */
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Key, Clock, Plus } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { sessionDialogEnvMapping, isDialogOpenable } from './session-credential-dialog.js'

export interface SessionCredential {
  requestId: string
  file?: string
  label?: string
  /** Keybook entry label (shown when bound) */
  entryLabel?: string
  envMapping: Record<string, string>
  entryId?: string
  status: 'pending' | 'active'
  /** Service this credential authenticates against — drives
   *  provider-aware UI (e.g. github → Authorize/PAT picker) when the
   *  user re-opens this credential from the popover. */
  provider?: string
  /** The call's field types (#1448 D2): its one-time (`prompt`) fields aren't the key's, so they aren't listed */
  fieldTypes?: Record<string, string>
}

interface SessionKeybookPopoverProps {
  credentials: SessionCredential[]
  onClose: () => void
  /** Ref to the trigger button for positioning */
  anchorRef?: React.RefObject<HTMLButtonElement | null>
  /** Called when user clicks a pending credential to fill it */
  onPendingClick?: (cred: SessionCredential) => void
  /** Called when user clicks "+ Add credential" — opens the add dialog */
  onAddClick?: () => void
}

export function SessionKeybookPopover({ credentials, onClose, anchorRef, onPendingClick, onAddClick }: SessionKeybookPopoverProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [pos, setPos] = useState<{ bottom: number; right: number } | null>(null)

  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect()
      // Position above the button, right-aligned so it doesn't overflow
      const right = window.innerWidth - rect.right
      setPos({
        bottom: window.innerHeight - rect.top + 8,
        right: Math.max(8, right),
      })
    }
  }, [anchorRef])

  // Close on outside click. Ignore clicks on the anchor itself (the
  // BookKey icon) — that button owns the toggle logic and would
  // otherwise immediately re-open the popover after close fires.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (popoverRef.current?.contains(target)) return
      if (anchorRef?.current?.contains(target)) return
      onClose()
    }
    // mousedown so it fires before any click inside can mutate state
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [onClose, anchorRef])

  // Also close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // a row of only one-time fields has nothing the dialog could fill (#1448 D2/D4): not listed
  const listed = credentials.filter(isDialogOpenable)
  const pending = listed.filter(c => c.status === 'pending')
  const active = listed.filter(c => c.status === 'active')

  const content = (
    <div
      ref={popoverRef}
      className={`${themeClass} fixed w-72 rounded-lg shadow-xl themed-bg-secondary border border-yellow-500/20 ring-1 ring-yellow-500/10 z-[60]`}
      style={pos ? { bottom: pos.bottom, right: 8 } : { bottom: 60, right: 8 }}
    >
      <div className="flex items-center gap-2 p-3 border-b border-yellow-500/15 bg-yellow-500/5">
        <Key size={13} className="text-yellow-400/80" />
        <span className="text-sm font-medium themed-text flex-1">Session Credentials</span>
        <button onClick={onClose} className="themed-text-muted hover:themed-text">
          <X size={14} />
        </button>
      </div>

      <div className="max-h-64 overflow-y-auto">
        {listed.length === 0 ? (
          <div className="p-4 text-center text-xs themed-text-muted">
            No credentials in this session.
            <br />
            AI will request credentials when needed, or add one manually below.
          </div>
        ) : (
          <>
            {pending.length > 0 && (
              <div className="p-2">
                <div className="text-[10px] uppercase tracking-wide themed-text-muted mb-1 px-1">Pending</div>
                {pending.map(cred => (
                  <button
                    key={cred.requestId}
                    onClick={() => onPendingClick?.(cred)}
                    className="w-full flex items-center gap-2 p-2 rounded themed-bg-primary mb-1 hover:themed-bg-tertiary transition-colors text-left"
                  >
                    <Clock size={12} className="text-yellow-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-yellow-400 truncate">{(cred.status === 'active' ? cred.entryLabel : null) || cred.label || cred.file?.split('/').pop() || 'Credentials'}</div>
                      <div className="text-[10px] themed-text-muted">Click to enter credentials</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {active.length > 0 && (
              <div className="p-2">
                <div className="text-[10px] uppercase tracking-wide themed-text-muted mb-1 px-1">Active</div>
                {active.map(cred => (
                  <button
                    key={cred.requestId}
                    onClick={() => onPendingClick?.(cred)}
                    className="w-full flex items-start gap-2 p-2 rounded themed-bg-primary mb-1 hover:themed-bg-tertiary transition-colors text-left"
                  >
                    <Key size={12} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs themed-text truncate">{(cred.status === 'active' ? cred.entryLabel : null) || cred.label || cred.file?.split('/').pop() || 'Credentials'}</div>
                      <div className="text-[10px] themed-text-muted font-mono">
                        {/* Dedupe — hidden providers alias every
                            declared field to the same internal env
                            var, so a naive list prints the same
                            $__PAPAN_CRED_xxx twice. */}
                        {/* #1448 D2: the key's variables only — a one-time field's isn't the key's (the server never fills it) */}
                        {Array.from(new Set(Object.values(sessionDialogEnvMapping(cred)))).map(v => `$${v}`).join(', ')}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {onAddClick && (
        <div className="border-t themed-border p-2">
          <button
            onClick={onAddClick}
            className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
          >
            <Plus size={12} />
            Add credential from keybook
          </button>
        </div>
      )}
    </div>
  )

  return createPortal(content, document.body)
}
